/**
 * @fileoverview Files Cell Factory
 *
 * Factory function for creating CellConfig arrays for file rows in UniversalTable.
 * Handles rendering of all file-related cells with proper event handlers and styling.
 */

import { Chip, Tooltip } from "@heroui/react";
import { Download, Info, TrashSimple, Lightning, User, ArrowUpRight } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { CellConfig } from "shared/components/UniversalTable/components";
import { Checkbox } from "@/components/ui/checkbox";
import { FileLog, FileLogStatus } from "shared/types/file";

// EPC Label enum (matching energy performance section)
enum EPCLabel {
  A_PLUS_PLUS = "A++",
  A_PLUS = "A+",
  A = "A",
  B = "B",
  C = "C",
  D = "D",
  E = "E",
  F = "F",
  G = "G",
  UNKNOWN = "Onbekend",
  NIET_VAN_TOEPASSING = "Niet van toepassing",
  GEMEENSCHAPPELIJKE_DELEN = "Gemeenschappelijke delen",
  ONTBREKEND = "Ontbrekend"
}

// EPC Label color mapping
const getEPCColor = (label: string) => {
  switch (label) {
    case EPCLabel.A_PLUS:
    case EPCLabel.A:
      return "bg-status-good text-white";
    case EPCLabel.B:
      return "bg-moss-primary text-white";
    case EPCLabel.C:
      return "bg-yellow-500 text-white";
    case EPCLabel.D:
      return "bg-orange-500 text-white";
    case EPCLabel.E:
    case EPCLabel.F:
      return "bg-brick-primary text-white";
    case EPCLabel.GEMEENSCHAPPELIJKE_DELEN:
      return "bg-neutral-primary text-white";
    default:
      return "bg-neutral-secondary-mid text-white";
  }
};

const getStatusForFile = (status: any) => {
  switch (status) {
    case FileLogStatus.OK:
      return "bg-green-500";
    case FileLogStatus.OK_WITH_REMARKS:
      return "bg-orange-500";
    case FileLogStatus.NOT_REQUIRED:
      return "bg-green-500";
    case FileLogStatus.NOT_OK:
      return "bg-red-500";
    default:
      return "bg-transparent-500";
  }
};

interface CreateFilesCellsParams {
  file: FileLog;
  isEditing: boolean;
  handlers: {
    onDelete: (file: FileLog) => void;
    onOpenEpcModal: (file: FileLog) => void;
    onEdit: (file: FileLog) => void;
    onSave: (fileId: number, updates: Partial<FileLog>) => Promise<void>;
    onCancel: () => void;
  };
  users: any[];
  context?: any;
}

export const createFilesCells = ({
  file,
  handlers,
  users,
  context
}: CreateFilesCellsParams): CellConfig[] => {
  const getUserFullName = (userId: any) => {
    const foundUser = users && users.find((user: any) => user.id == userId);
    return foundUser
      ? `${foundUser.first_name} ${foundUser.last_name}`
      : "Onbekende gebruiker";
  };

  // Check if this file should show EPC data based on labels
  const shouldShowEpcData = file.label?.some(label =>
    label === 'EPC unit' || label === 'EPC gemeenschap'
  );

  // Helper to call picker event
  const callPickerEvent = (mode: string, currentValue: any) => {
    context?.onPickerEvent?.({
      mode,
      fileIds: [file.id!],
      currentValue
    });
  };

  return [
    // Selection checkbox Column — lets the user pick individual rows for bulk
    // actions (e.g. ZIP download). Header renders the "select all on page" box.
    {
      id: 'checkbox',
      content: (
        <Checkbox
          checked={!!context?.isSelected}
          onCheckedChange={(checked) => context?.onToggleSelect?.(checked === true)}
          aria-label={`Selecteer bestand ${file.id}`}
        />
      ),
      align: 'center',
      className: 'flex justify-center',
      onClick: (e) => e.stopPropagation(),
    },

    // Status Column
    {
      id: 'status',
      content: (
        <Tooltip content={file.statusDescription || file.status || "Reden voor status niet ingevuld"}>
          <span
            className={`inline-block h-5 w-5 rounded-full border border-neutral-primary/30 cursor-pointer hover:scale-110 transition-transform ${getStatusForFile(file.status)}`}
          />
        </Tooltip>
      ),
      align: 'center',
      className: '',
      onClick: (e) => {
        e.stopPropagation();
        callPickerEvent('status', file.status);
      }
    },

    // Actions Column (right after status)
    {
      id: 'actions',
      content: (
        <div className="flex items-center gap-1.5 justify-center">
          <Tooltip content="Open bestand">
            <button
              onClick={(e) => {
                e.stopPropagation();
                file.sharePointFileUrl && window.open(file.sharePointFileUrl, "_blank");
              }}
              className="rounded-full border border-neutral-secondary-mid bg-transparent p-1.5 text-neutral-primary duration-250 hover:bg-neutral-secondary-mid"
            >
              <Download weight="duotone" size={16} />
            </button>
          </Tooltip>
          <Tooltip content="Verwijder bestand">
            <button
              onClick={(e) => {
                e.stopPropagation();
                handlers.onDelete(file);
              }}
              className="rounded-full border border-neutral-secondary-mid bg-transparent p-1.5 text-neutral-primary duration-250 hover:bg-brick-primary hover:text-white hover:border-brick-primary hover:shadow-md"
            >
              <TrashSimple weight="duotone" size={16} />
            </button>
          </Tooltip>
          <Tooltip
            content={
              <div className="flex flex-col gap-2 p-2">
                <p>
                  Labels: {file.label?.join(", ") || "Geen"}
                </p>
                <p>
                  Laatst gewijzigd:{" "}
                  {file.updatedAt
                    ? new Date(file.updatedAt).toLocaleString()
                    : file.createdAt
                    ? new Date(file.createdAt).toLocaleString()
                    : "Onbekend"}
                </p>
                <p>
                  Geüpload door:{" "}
                  {getUserFullName(file.createdBy) || "Onbekend"}
                </p>
              </div>
            }
          >
            <Info weight="duotone" size={16} className="cursor-help text-neutral-primary" />
          </Tooltip>
          {shouldShowEpcData && (
            <Tooltip content="Bewerk EPC metadata">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handlers.onOpenEpcModal(file);
                }}
                className="rounded-full border border-neutral-secondary-mid bg-transparent p-1.5 text-neutral-primary duration-250 hover:bg-neutral-secondary-mid"
              >
                <Lightning weight="duotone" size={16} />
              </button>
            </Tooltip>
          )}
        </div>
      ),
      align: 'center',
      className: ''
    },

    // ID Column
    {
      id: 'id',
      content: (
        <span className="text-sm text-neutral-primary">
          ID: {file.id}
        </span>
      ),
      align: 'left',
      className: ''
    },

    // Building Column
    {
      id: 'building',
      content: (
        <span className="text-sm text-neutral-primary">
          {file.building?.id || '-'}
        </span>
      ),
      align: 'left',
      className: ''
    },

    // Unit Column
    {
      id: 'unit',
      content: (
        <span className="text-sm text-neutral-primary">
          {file.unit?.id || '-'}
        </span>
      ),
      align: 'left',
      className: ''
    },

    // Company (Vennootschap) Column
    {
      id: 'company',
      content: (
        <span className="text-sm text-neutral-primary">
          {(file as any).company?.name ||
            (file as any).building?.company?.name ||
            '-'}
        </span>
      ),
      align: 'left',
      className: ''
    },

    // Renovation Project Column
    {
      id: 'renovationProject',
      content: (
        <span className="text-sm text-neutral-primary">
          {file.renovationProject?.title || '-'}
        </span>
      ),
      align: 'left',
      className: 'cursor-pointer hover:bg-neutral-secondary-light/30 transition-colors',
      onClick: (e) => {
        e.stopPropagation();
        callPickerEvent('renovation-project', file.renovationProject);
      }
    },

    // Project Part Column
    {
      id: 'projectPart',
      content: (
        <span className="text-sm text-neutral-primary">
          {file.projectPart?.title || '-'}
        </span>
      ),
      align: 'left',
      className: 'cursor-pointer hover:bg-neutral-secondary-light/30 transition-colors',
      onClick: (e) => {
        e.stopPropagation();
        callPickerEvent('project-part', file.projectPart);
      }
    },

    // Labels Column
    {
      id: 'labels',
      content: (
        <div className="flex flex-wrap gap-1">
          {file.label?.map((label, i) => (
            <Chip
              key={i}
              variant="flat"
              className={
                label === 'EPC unit' || label === 'EPC gemeenschap'
                  ? getEPCColor(label)
                  : "bg-moss-primary text-white"
              }
            >
              {label}
            </Chip>
          ))}
          {shouldShowEpcData && file.epc && (
            <Chip
              variant="flat"
              className={`${getEPCColor(file.epc.epcLabel || 'Onbekend')} cursor-pointer hover:opacity-80 transition-colors`}
              startContent={<Lightning size={14} />}
              onClick={(e) => {
                e.stopPropagation();
                handlers.onOpenEpcModal(file);
              }}
            >
              EPC {file.epc.epcLabel || 'Beschikbaar'}
              {file.epc.kWh && ` - ${file.epc.kWh} kWh/m²`}
            </Chip>
          )}
          {shouldShowEpcData && !file.epc && (
            <Chip
              variant="flat"
              className="bg-neutral-secondary-mid text-white cursor-pointer hover:bg-neutral-secondary-mid/80 transition-colors"
              startContent={<Lightning size={14} />}
              onClick={(e) => {
                e.stopPropagation();
                handlers.onOpenEpcModal(file);
              }}
            >
              EPC Niet ingevuld
            </Chip>
          )}
        </div>
      ),
      align: 'left',
      className: ''
    },

    // Document Date Column
    {
      id: 'documentDate',
      content: (
        <span className="text-sm text-neutral-primary">
          {file.documentDate
            ? new Date(file.documentDate).toLocaleDateString()
            : "n.v.t."}
        </span>
      ),
      align: 'left',
      className: 'cursor-pointer hover:bg-neutral-secondary-light/30 transition-colors',
      onClick: (e) => {
        e.stopPropagation();
        callPickerEvent('document-date', file.documentDate);
      }
    },

    // Expiration Date Column
    {
      id: 'expirationDate',
      content: (
        <span className="text-sm text-neutral-primary">
          {file.expirationDate
            ? new Date(file.expirationDate).toLocaleDateString()
            : "Onbekend"}
        </span>
      ),
      align: 'left',
      className: 'cursor-pointer hover:bg-neutral-secondary-light/30 transition-colors',
      onClick: (e) => {
        e.stopPropagation();
        callPickerEvent('expiration-date', file.expirationDate);
      }
    },

    // Summary Column
    {
      id: 'summary',
      content: (
        <span className="text-sm text-neutral-primary truncate max-w-[200px]">
          {file.summary || "Geen samenvatting ingevuld."}
        </span>
      ),
      align: 'left',
      className: 'cursor-pointer hover:bg-neutral-secondary-light/30 transition-colors',
      onClick: (e) => {
        e.stopPropagation();
        callPickerEvent('summary', file.summary);
      }
    },

    // Relations Column
    {
      id: 'relations',
      content: (
        <div className="flex flex-wrap gap-1 p-1">
          {file.relations && file.relations.length > 0 ? (
            file.relations.map((relation: any) => (
              <Chip
                key={relation.resourceId}
                startContent={<User weight="duotone" size={18} />}
                variant="flat"
                className="bg-white px-2 border border-moss-primary text-moss-primary"
              >
                <div className="flex items-center gap-1">
                  <Link
                    to={`/${relation.type}/${relation.resourceId}`}
                    className="text-moss-primary"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {relation.name}
                  </Link>
                  <ArrowUpRight className="h-4 w-4 ml-1" />
                </div>
              </Chip>
            ))
          ) : (
            <span className="text-sm text-neutral-secondary-mid italic">Geen relaties</span>
          )}
        </div>
      ),
      align: 'left',
      className: 'cursor-pointer hover:bg-neutral-secondary-light/30 transition-colors',
      onClick: (e) => {
        e.stopPropagation();
        callPickerEvent('relations', file.relations);
      }
    },

  ];
};
